import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupManager = ({ getAuthHeaders }) => {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [diskSpace, setDiskSpace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cleanupDays, setCleanupDays] = useState(90);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);

  useEffect(() => {
    fetchBackups();
    fetchStats();
    fetchDiskSpace();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/backup/list?limit=100`, getAuthHeaders());
      setBackups(response.data.backups || []);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
      setError('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch backup stats:', err);
    }
  };

  const fetchDiskSpace = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/disk-space`, getAuthHeaders());
      setDiskSpace(response.data);
    } catch (err) {
      console.error('Failed to fetch disk space:', err);
    }
  };

  const createBackup = async () => {
    if (!window.confirm('Create a new backup? This may take a few minutes.')) {
      return;
    }

    try {
      setCreating(true);
      setMessage('');
      setError('');
      
      const response = await axios.post(`${API}/admin/backup/create`, {}, getAuthHeaders());
      
      if (response.data.success) {
        setMessage('✅ Backup created successfully!');
        fetchBackups();
        fetchStats();
      } else {
        setError(response.data.message || 'Backup creation failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = (filename) => {
    const url = `${API}/admin/backup/download/${filename}`;
    const headers = getAuthHeaders().headers;
    
    // Create a temporary form to download with auth headers
    fetch(url, { headers })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(err => {
        setError('Failed to download backup');
        console.error(err);
      });
  };


  const deleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}? This cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setMessage('');
      
      const response = await axios.delete(
        `${API}/admin/backup/delete/${filename}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setMessage(`✅ Backup deleted successfully`);
        fetchBackups();
        fetchStats();
        fetchDiskSpace();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const cleanupOldBackups = async () => {
    if (!cleanupDays || cleanupDays < 1) {
      setError('Please enter a valid number of days');
      return;
    }

    if (!window.confirm(`Delete all backups older than ${cleanupDays} days? This cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setMessage('');
      setLoading(true);
      
      const response = await axios.post(
        `${API}/admin/backup/cleanup?days=${cleanupDays}`,
        {},
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setMessage(`✅ Deleted ${response.data.deleted_count} backups, freed ${response.data.freed_space_mb} MB`);
        setShowCleanupDialog(false);
        fetchBackups();
        fetchStats();
        fetchDiskSpace();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cleanup backups');
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Database Backups</h2>
            <p className="text-sm text-gray-600 mt-1">
              Automated daily backups at 2:00 AM ET. All backups are kept indefinitely.
            </p>
          </div>
          <button
            onClick={createBackup}
            disabled={creating}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {creating ? '⏳ Creating Backup...' : '📦 Create Backup Now'}
          </button>
        </div>

        {/* Storage Warning */}
        {diskSpace && diskSpace.warning_level === 'critical' && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🚨</span>
              <div>
                <div className="font-bold">CRITICAL: Storage Almost Full ({diskSpace.used_percent}%)</div>
                <div className="text-sm">Only {diskSpace.free_gb} GB remaining. Delete old backups immediately to prevent backup failures.</div>
              </div>
            </div>
          </div>
        )}
        {diskSpace && diskSpace.warning_level === 'warning' && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <div className="font-bold">Low Storage Space ({diskSpace.used_percent}%)</div>
                <div className="text-sm">{diskSpace.free_gb} GB remaining. Consider deleting old backups soon.</div>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {message}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Statistics */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Total Backups</div>
              <div className="text-2xl font-bold text-blue-900">{stats.total_backups}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-green-600 font-medium">Successful</div>
              <div className="text-2xl font-bold text-green-900">{stats.successful_backups}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-sm text-red-600 font-medium">Failed</div>
              <div className="text-2xl font-bold text-red-900">{stats.failed_backups}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-sm text-purple-600 font-medium">Total Size</div>
              <div className="text-2xl font-bold text-purple-900">{stats.total_size_mb} MB</div>
            </div>
          </div>
        )}
      </div>

      {/* Backup List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Backup History</h3>
          <button
            onClick={() => setShowCleanupDialog(!showCleanupDialog)}
            className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm font-medium"
          >
            🗑️ Cleanup Old Backups
          </button>
        </div>

        {/* Cleanup Dialog */}
        {showCleanupDialog && (
          <div className="px-6 py-4 bg-orange-50 border-b border-orange-200">
            <h4 className="font-semibold text-orange-900 mb-3">Delete Old Backups</h4>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delete backups older than (days):
                </label>
                <input
                  type="number"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(parseInt(e.target.value))}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <button
                onClick={cleanupOldBackups}
                disabled={loading}
                className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700 font-medium disabled:bg-gray-400"
              >
                Delete Backups
              </button>
              <button
                onClick={() => setShowCleanupDialog(false)}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400 font-medium"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-orange-700 mt-2">
              ⚠️ This action cannot be undone. Backups will be permanently deleted.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : backups.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No backups found. Create your first backup using the button above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filename</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {backups.map((backup, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">
                      {backup.filename}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(backup.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {backup.size_mb.toFixed(2)} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {backup.status === 'success' ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                          ✓ Success
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                          ✗ Failed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {backup.status === 'success' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => downloadBackup(backup.filename)}
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs font-medium"
                          >
                            📥 Download
                          </button>
                          <button
                            onClick={() => deleteBackup(backup.filename)}
                            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs font-medium"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <span className="text-gray-400 text-xs">
                            {backup.error_message || 'Backup failed'}
                          </span>
                          <button
                            onClick={() => deleteBackup(backup.filename)}
                            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-xs font-medium"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">📌 Backup Information & Safeguards</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Schedule:</strong> Automated backups run daily at 2:00 AM Eastern Time (ET)</li>
          <li>• <strong>Storage:</strong> {diskSpace ? `${diskSpace.free_gb} GB available of ${diskSpace.total_gb} GB total (${diskSpace.used_percent}% used)` : 'Loading...'}</li>
          <li>• <strong>Safeguards:</strong> Pre-backup space check requires minimum 500 MB free space</li>
          <li>• <strong>Alerts:</strong> Email notifications at 80% full (warning) and 90% full (critical)</li>
          <li>• <strong>Retention:</strong> All backups kept indefinitely unless manually deleted</li>
          <li>• <strong>Manual Actions:</strong> Create backup anytime, download, or delete individual backups</li>
          <li>• <strong>Bulk Cleanup:</strong> Use "Cleanup Old Backups" to delete backups older than specified days</li>
          <li>• <strong>Failure Alerts:</strong> Email sent to admin if any backup fails</li>
        </ul>
      </div>
    </div>
  );
};

export default BackupManager;
